import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { SavedSession, FeedbackDetail } from '../types';
import { User } from 'firebase/auth';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, Cell, PieChart, Pie
} from 'recharts';
import {
    TrendingUp, Users, Target, AlertCircle, Calendar, Clock, ChevronRight,
    ArrowUpRight, PlusCircle, Sparkles, Filter, Mail, MessageSquare, Mic, TrendingDown, Activity, ClipboardList
} from 'lucide-react';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
    greeting_and_opening: "Setting the right tone from the start. Focus on professional greetings and setting clear expectations.",
    active_listening_and_understanding: "Demonstrating that you hear and process user needs. Focus on clarifying questions and acknowledging key points.",
    empathy_and_tone: "The human side of service. Focus on validating emotions and maintaining a supportive, professional voice.",
    problem_solving_resolution: "Efficiency and effectiveness. Focus on finding actual solutions and minimizing friction for the customer.",
    communication_clarity: "Being understood. Focus on avoiding jargon, using concise language, and structured explanations.",
    compliance_and_accuracy: "Safety first. Focus on following protocols and providing 100% accurate information or data.",
    documentation_and_note_taking: "Leaving a trail. Focus on thorough summaries and structured notes for future reference.",
    closing: "Leaving a lasting impression. Focus on summarizing actions and confirming that all needs were met."
};

interface DashboardProps {
    user: User;
    onSessionClick: (session: SavedSession) => void;
    onStartNewSimulation: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ user, onSessionClick, onStartNewSimulation }) => {
    const [sessions, setSessions] = useState<SavedSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedChannel, setSelectedChannel] = useState<'All' | 'Voice' | 'Email' | 'Chat'>('All');
    const [selectedForm, setSelectedForm] = useState<string>('All');
    const [assessmentRubrics, setAssessmentRubrics] = useState<any[]>([]);
    const [userAllocInfo, setUserAllocInfo] = useState<{ used: number, alloc: number | 'unlimited' } | null>(null);

    useEffect(() => {
        const fetchSessions = async () => {
            setLoading(true);
            try {
                const q = query(
                    collection(db, "sessions"),
                    where("userId", "==", user.uid),
                    orderBy("timestamp", "desc"),
                    limit(50) // Increased limit for better trending
                );

                const querySnapshot = await getDocs(q);
                const fetchedSessions: SavedSession[] = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    fetchedSessions.push({
                        id: doc.id,
                        ...data,
                        timestampDate: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp)
                    } as any);
                });

                setSessions(fetchedSessions);

                // Fetch Assessment Forms
                const rubricsSnap = await getDocs(collection(db, 'assessmentRubrics'));
                const rubricsList = rubricsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                setAssessmentRubrics(rubricsList);

                // Fetch allocation
                const userDocRef = doc(db, 'users', user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const data = userDocSnap.data();
                    const alloc = data.allocatedMinutes !== undefined ? data.allocatedMinutes : 150;
                    const currentMonth = new Date().toISOString().slice(0, 7);
                    const used = data.usedMinutes?.[currentMonth] || 0;
                    setUserAllocInfo({ used, alloc });
                } else {
                    setUserAllocInfo({ used: 0, alloc: 150 });
                }

            } catch (error: any) {
                console.error("Error fetching sessions:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchSessions();
    }, [user.uid]);

    const availableChannels = useMemo(() => {
        const channels = new Set<string>();
        sessions.forEach(s => channels.add(s.settings.channel));
        return Array.from(channels);
    }, [sessions]);

    const filteredSessions = useMemo(() => {
        let result = sessions;
        if (selectedChannel !== 'All') {
            result = result.filter(s => s.settings.channel === selectedChannel);
        }
        if (selectedForm !== 'All') {
            result = result.filter(s => s.settings.assessmentRubricId === selectedForm || (selectedForm === 'generic' && !s.settings.assessmentRubricId));
        }
        return result;
    }, [sessions, selectedChannel, selectedForm]);

    const derivedStats = useMemo(() => {
        const data = filteredSessions;
        if (!data || data.length === 0) {
            return {
                avgScore: 0,
                totalSessions: 0,
                bestCategory: 'N/A',
                needsWorkCategory: 'N/A',
                scoreTrend: 0,
                isImproving: true
            };
        }

        const sessionsWithScores = data.filter(s => s.feedback && typeof s.feedback.overall_score === 'number');
        if (sessionsWithScores.length === 0) {
            return { avgScore: 0, totalSessions: data.length, bestCategory: 'N/A', needsWorkCategory: 'N/A', scoreTrend: 0, isImproving: true };
        }

        const totalScore = sessionsWithScores.reduce((sum, s) => sum + s.feedback.overall_score, 0);
        const avgScore = Math.round(totalScore / sessionsWithScores.length);

        // Trend calculation (compare latest session to average)
        const latestScore = sessionsWithScores[0].feedback.overall_score;
        const trend = latestScore - avgScore;

        const categoryTotals: Record<string, { total: number, count: number }> = {};
        sessionsWithScores.forEach(s => {
            const fb = s.feedback?.feedback;
            if (fb) {
                Object.entries(fb).forEach(([cat, detail]) => {
                    const d = detail as FeedbackDetail;
                    if (d && !d.not_applicable && typeof d.score === 'number' && typeof d.max_score === 'number' && d.max_score > 0) {
                        if (!categoryTotals[cat]) categoryTotals[cat] = { total: 0, count: 0 };
                        categoryTotals[cat].total += (d.score / d.max_score) * 100;
                        categoryTotals[cat].count += 1;
                    }
                });
            }
        });

        let best = '';
        let bestVal = -1;
        let worst = '';
        let worstVal = 101;

        Object.entries(categoryTotals).forEach(([cat, s]) => {
            const avg = s.total / s.count;
            if (avg > bestVal) {
                bestVal = avg;
                best = cat;
            }
            if (avg < worstVal) {
                worstVal = avg;
                worst = cat;
            }
        });

        const formatCat = (c: string) => c ? c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'N/A';

        return {
            avgScore,
            totalSessions: data.length,
            bestCategory: formatCat(best),
            needsWorkCategory: formatCat(worst),
            scoreTrend: Math.abs(trend),
            isImproving: trend >= 0
        };
    }, [filteredSessions]);

    const chartData = useMemo(() => {
        return filteredSessions
            .slice(0, 10)
            .reverse()
            .map((s, index) => {
                const dateObj = new Date((s as any).timestampDate);
                return {
                    id: s.id || `session-${index}`,
                    // Unique label for XAxis to prevent grouping
                    uniqueLabel: `${dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${dateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`,
                    // Display label for ticks
                    date: dateObj.toLocaleDateString([], { month: 'short', day: 'numeric' }),
                    score: s.feedback.overall_score || 0,
                    time: dateObj.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                };
            });
    }, [filteredSessions]);

    const categoryData = useMemo(() => {
        const categoryTotals: Record<string, { total: number, count: number }> = {};
        filteredSessions.forEach(s => {
            if (s.feedback?.feedback) {
                Object.entries(s.feedback.feedback).forEach(([cat, detail]) => {
                    const d = detail as FeedbackDetail;
                    if (!d.not_applicable && d.score !== null) {
                        const catName = cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        if (!categoryTotals[catName]) categoryTotals[catName] = { total: 0, count: 0 };
                        categoryTotals[catName].total += (d.score / d.max_score) * 100;
                        categoryTotals[catName].count += 1;
                    }
                });
            }
        });

        return Object.entries(categoryTotals).map(([name, s]) => ({
            name,
            score: Math.round(s.total / s.count)
        })).sort((a, b) => b.score - a.score);
    }, [filteredSessions]);

    const bottomCategories = useMemo(() => {
        const categoryTotals: Record<string, { total: number, count: number }> = {};
        filteredSessions.forEach(s => {
            if (s.feedback?.feedback) {
                Object.entries(s.feedback.feedback).forEach(([cat, detail]) => {
                    const d = detail as FeedbackDetail;
                    if (!d.not_applicable && d.score !== null) {
                        if (!categoryTotals[cat]) categoryTotals[cat] = { total: 0, count: 0 };
                        categoryTotals[cat].total += (d.score / d.max_score) * 100;
                        categoryTotals[cat].count += 1;
                    }
                });
            }
        });

        return Object.entries(categoryTotals)
            .map(([cat, s]) => ({
                id: cat,
                name: cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                score: Math.round(s.total / s.count)
            }))
            .sort((a, b) => a.score - b.score)
            .slice(0, 3);
    }, [filteredSessions]);

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <div className="loading">Initializing Dashboard...</div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="section-header" style={{ marginBottom: '1.5rem' }}>
                <div>
                    <h2 style={{ fontSize: '1.75rem' }}>Overview</h2>
                    <p style={{ color: 'var(--text-secondary)', marginTop: '0.25rem' }}>Welcome back, {user.email ? user.email.split('@')[0] : 'User'}</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    <div className="filter-pills" style={{ display: 'flex', background: 'var(--surface)', padding: '0.25rem', borderRadius: '12px', border: '1px solid var(--border)' }}>
                        {(['All', 'Voice', 'Chat', 'Email'] as const).map(ch => (
                            <button
                                key={ch}
                                className={`filter-pill ${selectedChannel === ch ? 'active' : ''}`}
                                onClick={() => setSelectedChannel(ch)}
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '8px',
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.4rem',
                                    transition: 'all 0.2s',
                                    background: selectedChannel === ch ? 'var(--primary)' : 'transparent',
                                    color: selectedChannel === ch ? 'white' : 'var(--text-secondary)',
                                    border: 'none'
                                }}
                            >
                                {ch === 'All' && <Filter size={14} />}
                                {ch === 'Voice' && <Mic size={14} />}
                                {ch === 'Chat' && <MessageSquare size={14} />}
                                {ch === 'Email' && <Mail size={14} />}
                                {ch}
                            </button>
                        ))}
                    </div>

                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <div style={{ position: 'absolute', left: '10px', color: 'var(--text-secondary)' }}>
                            <ClipboardList size={16} />
                        </div>
                        <select
                            className="input-fancy"
                            style={{ paddingLeft: '32px', margin: 0, paddingRight: '2rem', minWidth: '200px', cursor: 'pointer' }}
                            value={selectedForm}
                            onChange={(e) => setSelectedForm(e.target.value)}
                        >
                            <option value="All">All Forms</option>
                            <option value="generic">Generic Form (Default)</option>
                            {assessmentRubrics.map((r: any) => (
                                <option key={r.id} value={r.id}>
                                    {r.title}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button className="button button-primary" onClick={onStartNewSimulation}>
                        <PlusCircle size={18} />
                        Start New Session
                    </button>
                </div>
            </div>

            {userAllocInfo && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                    <div className="card" style={{ padding: '1.5rem', display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                        <div style={{ width: '120px', height: '120px', position: 'relative' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={[
                                            { name: 'Used', value: userAllocInfo.used },
                                            { name: 'Remaining', value: userAllocInfo.alloc === 'unlimited' ? 100 : Math.max(0, userAllocInfo.alloc - userAllocInfo.used) }
                                        ]}
                                        cx="50%" cy="50%" innerRadius={40} outerRadius={55} stroke="none"
                                        startAngle={90} endAngle={-270}
                                        dataKey="value"
                                    >
                                        <Cell fill="var(--primary)" />
                                        <Cell fill={userAllocInfo.alloc === 'unlimited' ? 'rgba(160,43,147,0.2)' : '#E5E7EB'} />
                                    </Pie>
                                </PieChart>
                            </ResponsiveContainer>
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <span style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--primary)' }}>{userAllocInfo.used.toFixed(1)}</span>
                                <span style={{ fontSize: '0.6rem', color: '#6B7280', textTransform: 'uppercase' }}>Mins</span>
                            </div>
                        </div>
                        <div>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: 800, color: '#1F2937', marginBottom: '0.2rem' }}>Session Usage</h3>
                            <p style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '0.75rem' }}>Current Month Utilization</p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <div style={{ background: 'rgba(160,43,147,0.1)', padding: '0.35rem 0.75rem', borderRadius: '2rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <Activity size={12} color="var(--primary)" />
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--primary)' }}>{userAllocInfo.used.toFixed(1)} Used</span>
                                </div>
                                <div style={{ background: '#F3F4F6', padding: '0.35rem 0.75rem', borderRadius: '2rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                    <Clock size={12} color="#6B7280" />
                                    <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#4B5563' }}>
                                        {userAllocInfo.alloc === 'unlimited' ? '∞ Allocated' : `${userAllocInfo.alloc} Allocated`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="dashboard-grid">
                <div className="stat-tile">
                    <span className="stat-label">Average Performance</span>
                    <span className="stat-value">{derivedStats.avgScore}%</span>
                    <div className={`stat-trend ${derivedStats.isImproving ? 'trend-up' : 'trend-down'}`}>
                        {derivedStats.isImproving ? <ArrowUpRight size={14} /> : <TrendingDown size={14} />}
                        <span>{derivedStats.scoreTrend}% vs average</span>
                    </div>
                    <TrendingUp className="stat-icon-bg" size={60} />
                </div>

                <div className="stat-tile">
                    <span className="stat-label">Total Simulations</span>
                    <span className="stat-value">{derivedStats.totalSessions}</span>
                    <div className="stat-trend">
                        <Calendar size={14} />
                        <span>Sessions Recorded</span>
                    </div>
                    <Users className="stat-icon-bg" size={60} />
                </div>

                <div className="stat-tile">
                    <span className="stat-label">Strongest Domain</span>
                    <span className="stat-value" style={{ fontSize: '1.25rem' }}>{derivedStats.bestCategory}</span>
                    <div className="stat-trend trend-up">
                        <Sparkles size={14} />
                        <span>Core Strength</span>
                    </div>
                    <Target className="stat-icon-bg" size={60} />
                </div>

                <div className="stat-tile">
                    <span className="stat-label">Focus Area</span>
                    <span className="stat-value" style={{ fontSize: '1.25rem' }}>{derivedStats.needsWorkCategory}</span>
                    <div className="stat-trend trend-down">
                        <AlertCircle size={14} />
                        <span>Growth Opportunity</span>
                    </div>
                    <AlertCircle className="stat-icon-bg" size={60} />
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2.5rem' }}>
                <div className="chart-card">
                    <h3>Performance Trend</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={chartData}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                                <XAxis
                                    dataKey="uniqueLabel"
                                    tickFormatter={(value, index) => {
                                        // Only show the date for the first entry of that day or if it's the first/last tick
                                        const item = chartData[index];
                                        if (!item) return value;
                                        return item.date;
                                    }}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{ fill: '#6B7280', fontSize: 10 }}
                                />
                                <YAxis domain={[0, 100]} axisLine={false} tickLine={false} tick={{ fill: '#6B7280', fontSize: 12 }} />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            const data = payload[0].payload;
                                            return (
                                                <div style={{ background: 'white', padding: '12px', borderRadius: '12px', border: '1px solid #E5E7EB', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                                    <p style={{ margin: 0, fontSize: '0.75rem', color: '#6B7280', fontWeight: 600 }}>{data.date} • {data.time}</p>
                                                    <p style={{ margin: '4px 0 0 0', fontSize: '1rem', color: 'var(--primary)', fontWeight: 800 }}>Score: {data.score}%</p>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="score"
                                    stroke="var(--primary)"
                                    strokeWidth={3}
                                    dot={{ fill: 'var(--primary)', strokeWidth: 2, r: 4 }}
                                    activeDot={{ r: 6, strokeWidth: 0 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="chart-card">
                    <h3>Category Mastery</h3>
                    <div className="chart-container">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={categoryData} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                                <XAxis type="number" domain={[0, 100]} hide />
                                <YAxis dataKey="name" type="category" width={120} axisLine={false} tickLine={false} tick={{ fill: '#1F2937', fontSize: 11, fontWeight: 500 }} />
                                <Tooltip cursor={{ fill: 'transparent' }} />
                                <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={20}>
                                    {categoryData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.score > 80 ? '#22c55e' : entry.score > 60 ? 'var(--primary)' : '#ef4444'} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
                <div>
                    <div className="section-header">
                        <h2>Recent History</h2>
                    </div>
                    <div className="sessions-list-container">
                        <div className="session-row session-header-row">
                            <div>Scenario</div>
                            <div>Date</div>
                            <div>Duration</div>
                            <div>Score</div>
                            <div></div>
                        </div>
                        {filteredSessions.length > 0 ? filteredSessions.map((s) => (
                            <div key={s.id} className="session-row" onClick={() => onSessionClick(s)}>
                                <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {s.settings.channel === 'Email' && <Mail size={16} style={{ color: 'var(--primary)' }} />}
                                    {s.settings.channel === 'Chat' && <MessageSquare size={16} style={{ color: 'var(--primary)' }} />}
                                    {s.settings.channel === 'Voice' && <Mic size={16} style={{ color: 'var(--primary)' }} />}
                                    {s.settings.scenarioType}
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <Calendar size={14} />
                                        {new Date((s as any).timestampDate).toLocaleDateString()}
                                    </span>
                                </div>
                                <div style={{ color: 'var(--text-secondary)' }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                        <Clock size={14} />
                                        {s.duration}
                                    </span>
                                </div>
                                <div>
                                    <span className={`score-pill ${s.feedback.overall_score >= 80 ? 'pill-high' : s.feedback.overall_score >= 60 ? 'pill-mid' : 'pill-low'}`}>
                                        {s.feedback.overall_score}%
                                    </span>
                                </div>
                                <div style={{ color: 'var(--text-muted)' }}><ChevronRight size={18} /></div>
                            </div>
                        )) : (
                            <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                {selectedChannel === 'All' ? "No practice sessions recorded yet. Start your first simulation!" : `No ${selectedChannel} sessions found.`}
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <div className="section-header">
                        <h2>Growth Insights</h2>
                    </div>
                    <div className="chart-card" style={{ padding: '1.5rem', border: '1px solid #FECDD3', background: '#FFF1F2' }}>
                        <div className="fb-points-box" style={{ marginTop: 0 }}>
                            {bottomCategories.length > 0 ? bottomCategories.map((cat, i) => (
                                <div key={i} className="fb-point fb-imp" style={{ borderLeft: '3px solid #FB7185', background: 'white', marginBottom: i === 2 ? 0 : '1rem' }}>
                                    <div>
                                        <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', marginBottom: '0.2rem', display: 'block', color: '#E11D48' }}>{cat.name} ({cat.score}%)</strong>
                                        <p style={{ fontSize: '0.85rem', lineHeight: '1.4', color: 'var(--text-primary)' }}>
                                            {CATEGORY_DESCRIPTIONS[cat.id] || "Continuous practice in this area is recommended to reach peak performance."}
                                        </p>
                                    </div>
                                </div>
                            )) : (
                                <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                                    Complete more simulations to unlock personalized growth insights.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
